import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Plus, FolderOpen, Calendar, ListChecks, Trash2, Pencil, Scale,
  Loader2, Sparkles, X, MapPin, Gavel, User, Hash, Clock, Compass, Calculator, ArrowRight,
  Archive, ArchiveRestore, Coins, Bell, ChevronDown,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { STATES, ROADMAP_STAGES, SAMPLE_CASE } from "@/data/legalData";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Progress } from "@/components/ui/progress";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const emptyCase = {
  title: "", jurisdiction: "State", state: "", court: "", judge: "",
  case_number: "", lead_charge: "", charge_level: "", stage: "arrest",
  attorney: "", notes: "", court_dates: [], checklist: [], archived: false,
};

const uid = () => Math.random().toString(36).slice(2, 10);

function daysUntil(dateStr) {
  if (!dateStr) return null;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const d = new Date(dateStr + "T00:00:00");
  return Math.round((d - today) / 86400000);
}

function countdown(dateStr) {
  const n = daysUntil(dateStr);
  if (n === null) return { label: "No date", tone: "muted" };
  if (n < 0) return { label: "Past", tone: "muted" };
  if (n === 0) return { label: "Today", tone: "urgent" };
  if (n === 1) return { label: "Tomorrow", tone: "urgent" };
  if (n <= 7) return { label: `In ${n} days`, tone: "soon" };
  return { label: `In ${n} days`, tone: "ok" };
}

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyCase);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showArchived, setShowArchived] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/cases");
      setCases(data);
    } catch { toast.error("Could not load your cases"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const startNew = () => { setForm(emptyCase); setEditingId(null); setOpen(true); };
  const startEdit = (c) => { setForm({ ...emptyCase, ...c }); setEditingId(c.case_id); setOpen(true); };

  const loadSample = async () => {
    setSaving(true);
    try {
      await api.post("/cases", SAMPLE_CASE);
      toast.success("Sample case added");
      load();
    } catch (e) {
      if (e.response?.status === 402) { toast.error(e.response.data.detail); navigate("/upgrade"); }
      else toast.error("Could not add sample");
    }
    finally { setSaving(false); }
  };

  const save = async () => {
    if (!form.title.trim()) { toast.error("Please give your case a title"); return; }
    setSaving(true);
    const payload = { ...form };
    try {
      if (editingId) { await api.put(`/cases/${editingId}`, payload); toast.success("Case updated"); }
      else { await api.post("/cases", payload); toast.success("Case saved"); }
      setOpen(false);
      load();
    } catch (e) {
      if (e.response?.status === 402) { setOpen(false); toast.error(e.response.data.detail); navigate("/upgrade"); }
      else toast.error("Could not save case");
    }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    try { await api.delete(`/cases/${id}`); setCases((c) => c.filter((x) => x.case_id !== id)); toast.success("Case deleted"); }
    catch { toast.error("Could not delete"); }
  };

  const toggleCheck = async (c, itemId) => {
    const checklist = c.checklist.map((it) => it.id === itemId ? { ...it, done: !it.done } : it);
    const updated = { ...c, checklist };
    setCases((list) => list.map((x) => x.case_id === c.case_id ? updated : x));
    try { await api.put(`/cases/${c.case_id}`, updated); } catch {}
  };

  const setArchived = async (c, val) => {
    const updated = { ...c, archived: val };
    setCases((list) => list.map((x) => x.case_id === c.case_id ? updated : x));
    try {
      await api.put(`/cases/${c.case_id}`, updated);
      toast.success(val ? "Case archived" : "Case restored");
    } catch { toast.error("Could not update"); load(); }
  };

  // form helpers for nested lists
  const addCourtDate = () => setForm((f) => ({ ...f, court_dates: [...f.court_dates, { id: uid(), label: "", date: "", notes: "" }] }));
  const setCourtDate = (id, k, v) => setForm((f) => ({ ...f, court_dates: f.court_dates.map((d) => d.id === id ? { ...d, [k]: v } : d) }));
  const rmCourtDate = (id) => setForm((f) => ({ ...f, court_dates: f.court_dates.filter((d) => d.id !== id) }));
  const addCheck = () => setForm((f) => ({ ...f, checklist: [...f.checklist, { id: uid(), text: "", done: false }] }));
  const setCheck = (id, v) => setForm((f) => ({ ...f, checklist: f.checklist.map((c) => c.id === id ? { ...c, text: v } : c) }));
  const rmCheck = (id) => setForm((f) => ({ ...f, checklist: f.checklist.filter((c) => c.id !== id) }));

  const activeCases = cases.filter((c) => !c.archived);
  const archivedCases = cases.filter((c) => c.archived);
  const upcoming = activeCases
    .flatMap((c) => (c.court_dates || []).map((d) => ({ ...d, caseTitle: c.title })))
    .filter((d) => d.date && daysUntil(d.date) >= 0)
    .sort((a, b) => a.date.localeCompare(b.date))
    .slice(0, 5);
  const nudges = upcoming.filter((d) => daysUntil(d.date) <= 3);

  const passExp = user?.case_pass_expires_at;
  const passDaysLeft = passExp ? Math.ceil((new Date(passExp) - Date.now()) / 86400000) : null;
  const passExpiring = user?.is_pro && user?.role !== "admin" && passDaysLeft !== null && passDaysLeft >= 0 && passDaysLeft <= 7;

  return (
    <div className="grain-bg min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
          <div>
            <span className="font-mono text-xs uppercase tracking-[0.18em] text-terracotta">Confidential case vault</span>
            <h1 className="mt-2 font-serif text-4xl sm:text-5xl font-semibold text-forest-900">Welcome, {user?.name?.split(" ")[0]}</h1>
            <p className="mt-2 text-ink-700">Track your charges, court dates, and questions for your lawyer — all in one private place.</p>
          </div>
          <Button onClick={startNew} data-testid="add-case-btn" className="rounded-full h-11 px-5 bg-forest-800 hover:bg-forest-900 text-parchment-50 gap-2">
            <Plus className="h-4 w-4" /> Add a case
          </Button>
        </div>

        {/* Case Pass renewal reminder */}
        {passExpiring && (
          <div data-testid="pass-renew-banner" className="mt-6 rounded-2xl border border-forest-600/30 bg-forest-50 p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <span className="grid place-items-center h-10 w-10 rounded-xl bg-forest-800 text-parchment-50"><Sparkles className="h-5 w-5" /></span>
              <div>
                <p className="font-semibold text-forest-900">{passDaysLeft === 0 ? "Your Case Pass expires today" : `Your Case Pass expires in ${passDaysLeft} day${passDaysLeft === 1 ? "" : "s"}`}</p>
                <p className="text-sm text-ink-600">Renew now to keep unlimited AI, exports, and reminders without interruption.</p>
              </div>
            </div>
            <Link to="/upgrade" data-testid="renew-pass-btn">
              <Button className="rounded-full bg-terracotta hover:bg-terracotta-hover text-white gap-2 shrink-0"><Sparkles className="h-4 w-4" /> Renew Case Pass</Button>
            </Link>
          </div>
        )}

        {/* Deadline nudge banner */}
        {nudges.length > 0 && (
          <div data-testid="deadline-nudge" className="mt-6 rounded-2xl border-l-4 border-terracotta bg-terracotta-subtle p-4 flex items-start gap-3">
            <Bell className="h-5 w-5 text-terracotta mt-0.5 shrink-0" />
            <div>
              <p className="font-semibold text-[#8C3415]">{nudges.length === 1 ? "A court date is coming up soon" : `${nudges.length} court dates are coming up soon`}</p>
              <div className="mt-1 space-y-0.5">
                {nudges.map((d) => (
                  <p key={d.id} className="text-sm text-[#8C3415]">
                    <strong>{countdown(d.date).label}</strong> — {d.label || "Court date"} · {d.caseTitle} · {d.date}
                  </p>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Tools */}
        <div className="mt-8 grid sm:grid-cols-3 gap-5">
          <Link to="/diagnostic" data-testid="tool-diagnostic" className="group rounded-2xl border border-parchment-200 bg-white p-5 hover:-translate-y-0.5 hover:shadow-md transition-all">
            <div className="flex items-center gap-3">
              <span className="grid place-items-center h-11 w-11 rounded-xl bg-forest-100 text-forest-800 group-hover:bg-forest-800 group-hover:text-parchment-50 transition-colors"><Compass className="h-5 w-5" /></span>
              <div>
                <h3 className="font-semibold text-forest-900">Case Diagnostic</h3>
                <p className="text-xs text-ink-500">Get a tailored next-steps plan</p>
              </div>
            </div>
          </Link>
          <Link to="/eligibility" data-testid="tool-eligibility" className="group rounded-2xl border border-parchment-200 bg-white p-5 hover:-translate-y-0.5 hover:shadow-md transition-all">
            <div className="flex items-center gap-3">
              <span className="grid place-items-center h-11 w-11 rounded-xl bg-forest-100 text-forest-800 group-hover:bg-forest-800 group-hover:text-parchment-50 transition-colors"><Calculator className="h-5 w-5" /></span>
              <div>
                <h3 className="font-semibold text-forest-900">Eligibility Calculator</h3>
                <p className="text-xs text-ink-500">Might you qualify for a free lawyer?</p>
              </div>
            </div>
          </Link>
          <Link to="/bail" data-testid="tool-bail" className="group rounded-2xl border border-parchment-200 bg-white p-5 hover:-translate-y-0.5 hover:shadow-md transition-all">
            <div className="flex items-center gap-3">
              <span className="grid place-items-center h-11 w-11 rounded-xl bg-forest-100 text-forest-800 group-hover:bg-forest-800 group-hover:text-parchment-50 transition-colors"><Coins className="h-5 w-5" /></span>
              <div>
                <h3 className="font-semibold text-forest-900">Bail Estimator</h3>
                <p className="text-xs text-ink-500">Likely release &amp; bail options</p>
              </div>
            </div>
          </Link>
        </div>

        {/* Next deadline */}
        <div data-testid="upcoming-deadlines" className="mt-5 rounded-2xl border border-parchment-200 bg-forest-900 text-parchment-50 p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Clock className="h-4 w-4 text-sage-500" />
            <span className="font-mono text-xs uppercase tracking-wider text-parchment-200/80">Next deadline</span>
          </div>
          {upcoming.length === 0 ? (
            <p className="text-sm text-parchment-200/70">No upcoming court dates yet — add them to a case to see live countdowns.</p>
          ) : (
            <div className="flex items-center gap-3 flex-wrap">
              <span className="font-serif text-xl font-semibold">{upcoming[0].label || "Court date"}</span>
              <span className="text-sm text-parchment-200/70">{upcoming[0].caseTitle}</span>
              <span className="rounded-full bg-terracotta text-white text-xs font-semibold px-3 py-1">{countdown(upcoming[0].date).label} · {upcoming[0].date}</span>
            </div>
          )}
        </div>

        {loading ? (
          <div className="mt-16 grid place-items-center"><Loader2 className="h-8 w-8 animate-spin text-forest-700" /></div>
        ) : cases.length === 0 ? (
          <div className="mt-12 rounded-3xl border-2 border-dashed border-parchment-300 bg-white p-12 text-center">
            <span className="grid place-items-center h-16 w-16 rounded-2xl bg-forest-100 text-forest-800 mx-auto"><FolderOpen className="h-8 w-8" /></span>
            <h2 className="mt-5 font-serif text-2xl font-semibold text-forest-900">Your vault is empty</h2>
            <p className="mt-2 text-ink-500 max-w-md mx-auto">Add your case details to start tracking, or load a sample case to explore how it works.</p>
            <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
              <Button onClick={startNew} className="rounded-full bg-forest-800 hover:bg-forest-900 text-parchment-50 gap-2"><Plus className="h-4 w-4" /> Add my case</Button>
              <Button onClick={loadSample} data-testid="load-sample-btn" variant="outline" disabled={saving} className="rounded-full border-forest-800/20 text-forest-800 gap-2">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} Load a sample case
              </Button>
            </div>
          </div>
        ) : (
          <div className="mt-10 grid lg:grid-cols-2 gap-6">
            {activeCases.map((c) => {
              const stageObj = ROADMAP_STAGES.find((s) => s.id === c.stage);
              const done = c.checklist.filter((x) => x.done).length;
              const pct = c.checklist.length ? (done / c.checklist.length) * 100 : 0;
              return (
                <motion.div key={c.case_id} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}
                  data-testid={`case-card-${c.case_id}`} className="rounded-3xl border border-parchment-200 bg-white p-6">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <h3 className="font-serif text-2xl font-semibold text-forest-900 leading-tight">{c.title}</h3>
                      {c.lead_charge && <p className="mt-1 text-sm text-ink-700">{c.lead_charge}{c.charge_level ? ` · ${c.charge_level}` : ""}</p>}
                    </div>
                    <div className="flex gap-1 shrink-0">
                      <Button onClick={() => setArchived(c, true)} data-testid={`archive-case-${c.case_id}`} size="icon" variant="ghost" className="h-8 w-8 text-ink-500 hover:text-forest-800"><Archive className="h-4 w-4" /></Button>
                      <Button onClick={() => startEdit(c)} data-testid={`edit-case-${c.case_id}`} size="icon" variant="ghost" className="h-8 w-8 text-ink-500 hover:text-forest-800"><Pencil className="h-4 w-4" /></Button>
                      <Button onClick={() => remove(c.case_id)} data-testid={`delete-case-${c.case_id}`} size="icon" variant="ghost" className="h-8 w-8 text-ink-500 hover:text-destructive"><Trash2 className="h-4 w-4" /></Button>
                    </div>
                  </div>

                  <div className="mt-4 flex flex-wrap gap-2">
                    {c.state && <Badge icon={MapPin}>{c.state}</Badge>}
                    {stageObj && <Badge icon={Gavel}>{stageObj.title}</Badge>}
                    {c.case_number && <Badge icon={Hash}>{c.case_number}</Badge>}
                    {c.judge && <Badge icon={User}>{c.judge}</Badge>}
                  </div>

                  {c.court_dates.length > 0 && (
                    <div className="mt-5">
                      <div className="flex items-center gap-2 text-forest-700 mb-2"><Calendar className="h-4 w-4" /><span className="font-mono text-xs uppercase tracking-wider">Upcoming dates</span></div>
                      <div className="space-y-2">
                        {c.court_dates.map((d) => {
                          const cd = countdown(d.date);
                          const toneCls = cd.tone === "urgent" ? "bg-terracotta text-white"
                            : cd.tone === "soon" ? "bg-[#FFFBEB] text-[#8a5a12] border border-[#F0D9A8]"
                            : cd.tone === "ok" ? "bg-forest-100 text-forest-800" : "bg-parchment-200 text-ink-500";
                          return (
                            <div key={d.id} className="flex items-center justify-between rounded-xl bg-parchment-50 border border-parchment-200 px-3 py-2">
                              <span className="text-sm text-ink-700">{d.label || "Court date"}</span>
                              <div className="flex items-center gap-2">
                                <span className="font-mono text-xs text-forest-700">{d.date || "TBD"}</span>
                                <span className={`text-[10px] font-semibold rounded-full px-2 py-0.5 ${toneCls}`}>{cd.label}</span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {c.checklist.length > 0 && (
                    <div className="mt-5">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2 text-forest-700"><ListChecks className="h-4 w-4" /><span className="font-mono text-xs uppercase tracking-wider">Prep checklist</span></div>
                        <span className="text-xs text-ink-400">{done}/{c.checklist.length}</span>
                      </div>
                      <Progress value={pct} className="h-1.5 mb-3" />
                      <div className="space-y-1.5">
                        {c.checklist.map((it) => (
                          <label key={it.id} className="flex items-start gap-2.5 cursor-pointer group">
                            <Checkbox checked={it.done} onCheckedChange={() => toggleCheck(c, it.id)} data-testid={`check-${it.id}`} className="mt-0.5" />
                            <span className={`text-sm ${it.done ? "line-through text-ink-400" : "text-ink-700"}`}>{it.text}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  )}

                  {c.notes && <p className="mt-5 text-sm text-ink-500 leading-relaxed border-t border-parchment-200 pt-4">{c.notes}</p>}
                </motion.div>
              );
            })}
          </div>
        )}

        {/* Archived cases */}
        {archivedCases.length > 0 && (
          <div className="mt-10">
            <button onClick={() => setShowArchived(!showArchived)} data-testid="toggle-archived"
              className="flex items-center gap-2 text-ink-500 hover:text-forest-800 transition-colors">
              <Archive className="h-4 w-4" />
              <span className="font-mono text-xs uppercase tracking-wider">Archived ({archivedCases.length})</span>
              <ChevronDown className={`h-4 w-4 transition-transform ${showArchived ? "rotate-180" : ""}`} />
            </button>
            {showArchived && (
              <div className="mt-4 space-y-2">
                {archivedCases.map((c) => (
                  <div key={c.case_id} data-testid={`archived-case-${c.case_id}`}
                    className="flex items-center justify-between rounded-2xl border border-parchment-200 bg-parchment-50 px-4 py-3">
                    <div className="min-w-0">
                      <p className="font-medium text-ink-700 truncate">{c.title}</p>
                      {c.lead_charge && <p className="text-xs text-ink-400 truncate">{c.lead_charge}</p>}
                    </div>
                    <div className="flex gap-1 shrink-0">
                      <Button onClick={() => setArchived(c, false)} data-testid={`restore-case-${c.case_id}`} variant="ghost" size="sm" className="gap-1.5 text-forest-700 hover:bg-forest-100">
                        <ArchiveRestore className="h-4 w-4" /> Restore
                      </Button>
                      <Button onClick={() => remove(c.case_id)} data-testid={`delete-archived-${c.case_id}`} variant="ghost" size="icon" className="h-9 w-9 text-ink-400 hover:text-destructive">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Case editor dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto thin-scroll bg-parchment-50 border-parchment-200">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-forest-900 flex items-center gap-2">
              <Scale className="h-5 w-5 text-forest-700" /> {editingId ? "Edit case" : "New case"}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 mt-2">
            <Field label="Case title *">
              <Input data-testid="case-title-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. My case — People v. …" className="rounded-xl border-parchment-200 bg-white" />
            </Field>

            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Jurisdiction">
                <Select value={form.jurisdiction} onValueChange={(v) => setForm({ ...form, jurisdiction: v })}>
                  <SelectTrigger className="rounded-xl border-parchment-200 bg-white"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="State">State</SelectItem><SelectItem value="Federal">Federal</SelectItem></SelectContent>
                </Select>
              </Field>
              <Field label="State">
                <Select value={form.state} onValueChange={(v) => setForm({ ...form, state: v })}>
                  <SelectTrigger data-testid="case-state-select" className="rounded-xl border-parchment-200 bg-white"><SelectValue placeholder="Select state" /></SelectTrigger>
                  <SelectContent className="max-h-64">{STATES.map((s) => <SelectItem key={s.abbr} value={s.name}>{s.name}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Lead charge"><Input value={form.lead_charge} onChange={(e) => setForm({ ...form, lead_charge: e.target.value })} placeholder="e.g. Grand Larceny" className="rounded-xl border-parchment-200 bg-white" /></Field>
              <Field label="Charge level"><Input value={form.charge_level} onChange={(e) => setForm({ ...form, charge_level: e.target.value })} placeholder="e.g. Class E Felony" className="rounded-xl border-parchment-200 bg-white" /></Field>
            </div>

            <Field label="Current stage">
              <Select value={form.stage} onValueChange={(v) => setForm({ ...form, stage: v })}>
                <SelectTrigger data-testid="case-stage-select" className="rounded-xl border-parchment-200 bg-white"><SelectValue /></SelectTrigger>
                <SelectContent>{ROADMAP_STAGES.map((s) => <SelectItem key={s.id} value={s.id}>{s.n}. {s.title}</SelectItem>)}</SelectContent>
              </Select>
            </Field>

            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Court"><Input value={form.court} onChange={(e) => setForm({ ...form, court: e.target.value })} className="rounded-xl border-parchment-200 bg-white" /></Field>
              <Field label="Judge"><Input value={form.judge} onChange={(e) => setForm({ ...form, judge: e.target.value })} className="rounded-xl border-parchment-200 bg-white" /></Field>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Case number"><Input value={form.case_number} onChange={(e) => setForm({ ...form, case_number: e.target.value })} className="rounded-xl border-parchment-200 bg-white" /></Field>
              <Field label="Attorney"><Input value={form.attorney} onChange={(e) => setForm({ ...form, attorney: e.target.value })} className="rounded-xl border-parchment-200 bg-white" /></Field>
            </div>

            {/* Court dates */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-ink-700">Court dates</Label>
                <Button type="button" size="sm" variant="ghost" onClick={addCourtDate} data-testid="add-court-date" className="text-forest-700 gap-1"><Plus className="h-3.5 w-3.5" /> Add</Button>
              </div>
              <div className="space-y-2">
                {form.court_dates.map((d) => (
                  <div key={d.id} className="flex gap-2 items-center">
                    <Input value={d.label} onChange={(e) => setCourtDate(d.id, "label", e.target.value)} placeholder="Label" className="rounded-xl border-parchment-200 bg-white" />
                    <Input type="date" value={d.date} onChange={(e) => setCourtDate(d.id, "date", e.target.value)} className="rounded-xl border-parchment-200 bg-white w-40" />
                    <Button type="button" size="icon" variant="ghost" onClick={() => rmCourtDate(d.id)} className="text-ink-400 hover:text-destructive shrink-0"><X className="h-4 w-4" /></Button>
                  </div>
                ))}
              </div>
            </div>

            {/* Checklist */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-ink-700">Prep checklist</Label>
                <Button type="button" size="sm" variant="ghost" onClick={addCheck} data-testid="add-checklist-item" className="text-forest-700 gap-1"><Plus className="h-3.5 w-3.5" /> Add</Button>
              </div>
              <div className="space-y-2">
                {form.checklist.map((c) => (
                  <div key={c.id} className="flex gap-2 items-center">
                    <Input value={c.text} onChange={(e) => setCheck(c.id, e.target.value)} placeholder="e.g. Collect character references" className="rounded-xl border-parchment-200 bg-white" />
                    <Button type="button" size="icon" variant="ghost" onClick={() => rmCheck(c.id)} className="text-ink-400 hover:text-destructive shrink-0"><X className="h-4 w-4" /></Button>
                  </div>
                ))}
              </div>
            </div>

            <Field label="Notes"><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={3} placeholder="Private notes about your case…" className="rounded-xl border-parchment-200 bg-white resize-none" /></Field>
          </div>

          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-xl border-parchment-200">Cancel</Button>
            <Button onClick={save} data-testid="save-case-btn" disabled={saving} className="rounded-xl bg-forest-800 hover:bg-forest-900 text-parchment-50 gap-2">
              {saving && <Loader2 className="h-4 w-4 animate-spin" />} {editingId ? "Save changes" : "Save case"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div><Label className="text-ink-700 mb-1.5 block">{label}</Label>{children}</div>
);

const Badge = ({ icon: Icon, children }) => (
  <span className="inline-flex items-center gap-1.5 rounded-full bg-forest-100 text-forest-800 px-3 py-1 text-xs font-medium">
    <Icon className="h-3.5 w-3.5" /> {children}
  </span>
);
